/**
 * Pure utilities to compute warnings before changing a reservation's table
 * (either via a table-change or via a swap between two reservations).
 *
 * No React, no Supabase. Side-effect free.
 *
 * Used by the floor-plan V2 confirm dialogs (étape 4.3 / 4.4).
 */

import type {
  CombinedTable,
  Reservation,
  Table,
  TableAvailability,
  TableBookablePeriod,
  TableWeekdayAvailability,
} from "@/types/table";

export type WarningLevel = "info" | "warn" | "block";

export type WarningCode =
  | "capacity"
  | "min_capacity"
  | "terrace"
  | "unavailable_inactive"
  | "unavailable_weekday"
  | "unavailable_blocked_date"
  | "unavailable_outside_bookable"
  | "overlap";

export interface MoveWarning {
  level: WarningLevel;
  code: WarningCode;
  message: string;
}

export type BookingMode = "slot" | "time";

/** A target table can be a simple table or a combined one — we normalize. */
export interface TargetTable {
  kind: "simple" | "combined";
  id: string;
  /** Physical sub-table ids (length 1 for simple, N for combined). */
  physicalIds: string[];
  name: string;
  capacity: number;
  minCapacity?: number;
  isActive: boolean;
  isBookable: boolean;
  isTerrace?: boolean;
}

export interface ComputeChangeInput {
  reservation: Pick<
    Reservation,
    | "id"
    | "supabaseId"
    | "numberOfPeople"
    | "terracePreference"
    | "durationMinutes"
    | "startTime"
    | "date"
    | "status"
  > & { tableId?: string; combinedTableId?: string };
  target: TargetTable;
  /** All reservations of the same date (active or not — we filter inside). */
  allReservations: Array<
    Pick<
      Reservation,
      "supabaseId" | "id" | "startTime" | "status" | "durationMinutes" | "date"
    > & { tableId?: string; combinedTableId?: string }
  >;
  /** Map combinedTableId → physical table ids, for overlap resolution. */
  combinedTablesById: Map<string, string[]>;
  bookingMode: BookingMode;
  defaultDurationMinutes: number;
  /** Specific date blockers for tables (TableAvailability rows where isAvailable === false). */
  blockedDates: TableAvailability[];
  /** Weekday availability rows (restricts to specific days). */
  weekdayAvailabilities: TableWeekdayAvailability[];
  /** Temporary bookable periods (if any row exists for a table, only those dates are bookable). */
  bookablePeriods: TableBookablePeriod[];
}

const HHMM_TO_MIN = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Build the list of physical ids for a reservation assignment. */
export function resolvePhysicalIds(
  tableId: string | undefined,
  combinedTableId: string | undefined,
  combinedTablesById: Map<string, string[]>,
): string[] {
  if (tableId) return [tableId];
  if (combinedTableId) return combinedTablesById.get(combinedTableId) ?? [];
  return [];
}

/** Normalize a Table or CombinedTable into a TargetTable. */
export function toTargetTable(
  source: Table | CombinedTable,
  kind: "simple" | "combined",
  combinedTablesById?: Map<string, string[]>,
): TargetTable {
  if (kind === "simple") {
    const t = source as Table;
    return {
      kind,
      id: t.id,
      physicalIds: [t.id],
      name: t.name,
      capacity: t.capacity,
      minCapacity: t.minCapacity,
      isActive: t.isActive,
      isBookable: t.isBookable,
      isTerrace: t.isTerrace,
    };
  }
  const c = source as CombinedTable;
  return {
    kind,
    id: c.id,
    physicalIds: combinedTablesById?.get(c.id) ?? c.tableIds ?? [],
    name: c.name,
    capacity: c.totalCapacity,
    minCapacity: c.minCapacity,
    isActive: c.isActive,
    isBookable: c.isBookable,
    isTerrace: c.isTerrace,
  };
}

/**
 * Compute warnings for moving `reservation` onto `target`.
 * Excludes the reservation itself from overlap checks (so re-assigning to its
 * current table is a no-op and never triggers a self-overlap).
 */
export function computeChangeWarnings(input: ComputeChangeInput): MoveWarning[] {
  const {
    reservation,
    target,
    allReservations,
    combinedTablesById,
    bookingMode,
    defaultDurationMinutes,
    blockedDates,
    weekdayAvailabilities,
    bookablePeriods,
  } = input;

  const warnings: MoveWarning[] = [];
  const resDate = reservation.date instanceof Date ? reservation.date : new Date(reservation.date);

  // 1. Capacity (signal, non-blocking)
  if (reservation.numberOfPeople > target.capacity) {
    warnings.push({
      level: "warn",
      code: "capacity",
      message: `${reservation.numberOfPeople} pers. sur une table de ${target.capacity} (capacité dépassée).`,
    });
  }

  // 1bis. Min capacity (info, non-blocking)
  if (target.minCapacity && reservation.numberOfPeople < target.minCapacity) {
    warnings.push({
      level: "info",
      code: "min_capacity",
      message: `Cette table requiert au minimum ${target.minCapacity} personne(s).`,
    });
  }

  // 2. Terrace mismatch (info)
  const pref = reservation.terracePreference;
  if (pref === "terrace" && !target.isTerrace) {
    warnings.push({
      level: "info",
      code: "terrace",
      message: "Le client a demandé la terrasse — table cible en intérieur.",
    });
  } else if (pref === "indoor" && target.isTerrace) {
    warnings.push({
      level: "info",
      code: "terrace",
      message: "Le client a demandé l'intérieur — table cible en terrasse.",
    });
  }

  // 3. Availability blockers (blocking)
  if (!target.isActive) {
    warnings.push({
      level: "block",
      code: "unavailable_inactive",
      message: "Cette table est désactivée.",
    });
  }

  // Weekday availability (only applies to simple tables)
  if (target.kind === "simple") {
    const wd = weekdayAvailabilities.find((w) => w.tableId === target.id);
    if (wd && wd.availableDays.length > 0) {
      const dow = resDate.getDay();
      if (!wd.availableDays.includes(dow)) {
        warnings.push({
          level: "block",
          code: "unavailable_weekday",
          message: "Cette table n'est pas disponible ce jour de la semaine.",
        });
      }
    }
  }

  // Specific date blockers (per physical sub-table)
  const dateBlocked = blockedDates.some(
    (b) =>
      target.physicalIds.includes(b.tableId) &&
      sameDay(b.date instanceof Date ? b.date : new Date(b.date), resDate) &&
      b.isAvailable === false,
  );
  if (dateBlocked) {
    warnings.push({
      level: "block",
      code: "unavailable_blocked_date",
      message: "Cette table est bloquée pour cette date.",
    });
  }

  // Bookable periods: if a table has any period defined, the date must fall inside one.
  for (const subId of target.physicalIds) {
    const periods = bookablePeriods.filter((p) => p.tableId === subId);
    if (periods.length === 0) continue;
    const inside = periods.some((p) => {
      const s = p.startDate instanceof Date ? p.startDate : new Date(p.startDate);
      const e = p.endDate instanceof Date ? p.endDate : new Date(p.endDate);
      return resDate >= s && resDate <= e;
    });
    if (!inside) {
      warnings.push({
        level: "block",
        code: "unavailable_outside_bookable",
        message: "Cette table n'est pas dans une période réservable pour cette date.",
      });
      break;
    }
  }

  // 4. Physical overlap (blocking, client-side preview of server trigger)
  const newDuration = reservation.durationMinutes ?? defaultDurationMinutes;
  const newStart = HHMM_TO_MIN(reservation.startTime);
  const newEnd = newStart + newDuration;

  const selfId = reservation.supabaseId ?? String(reservation.id);
  const targetSet = new Set(target.physicalIds);

  const overlapping = allReservations.find((r) => {
    const rId = r.supabaseId ?? String(r.id);
    if (rId === selfId) return false;
    if (r.status !== "planifié" && r.status !== "arrivé") return false;
    const rDate = r.date instanceof Date ? r.date : new Date(r.date);
    if (!sameDay(rDate, resDate)) return false;

    const rPhysical = resolvePhysicalIds(r.tableId, r.combinedTableId, combinedTablesById);
    if (rPhysical.length === 0) return false;
    if (!rPhysical.some((p) => targetSet.has(p))) return false;

    if (bookingMode === "time") {
      const rDur = r.durationMinutes ?? defaultDurationMinutes;
      const rStart = HHMM_TO_MIN(r.startTime);
      const rEnd = rStart + rDur;
      return newStart < rEnd && rStart < newEnd;
    }
    return r.startTime === reservation.startTime;
  });

  if (overlapping) {
    warnings.push({
      level: "block",
      code: "overlap",
      message: "Une autre réservation occupe déjà cette table sur ce créneau.",
    });
  }

  return warnings;
}

export interface ComputeSwapInput {
  a: ComputeChangeInput["reservation"];
  b: ComputeChangeInput["reservation"];
  /** target for A = current table of B, and vice-versa. */
  aTarget: TargetTable;
  bTarget: TargetTable;
  allReservations: ComputeChangeInput["allReservations"];
  combinedTablesById: Map<string, string[]>;
  bookingMode: BookingMode;
  defaultDurationMinutes: number;
  blockedDates: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
}

/**
 * Compute warnings for swapping reservations A and B.
 * The overlap check ignores BOTH reservations (because the RPC swaps atomically:
 * during the swap both assignments are cleared first).
 */
export function computeSwapWarnings(
  input: ComputeSwapInput,
): { aWarnings: MoveWarning[]; bWarnings: MoveWarning[] } {
  const baseAllReservations = input.allReservations.filter((r) => {
    const id = r.supabaseId ?? String(r.id);
    const aId = input.a.supabaseId ?? String(input.a.id);
    const bId = input.b.supabaseId ?? String(input.b.id);
    return id !== aId && id !== bId;
  });

  const shared = {
    allReservations: baseAllReservations,
    combinedTablesById: input.combinedTablesById,
    bookingMode: input.bookingMode,
    defaultDurationMinutes: input.defaultDurationMinutes,
    blockedDates: input.blockedDates,
    weekdayAvailabilities: input.weekdayAvailabilities,
    bookablePeriods: input.bookablePeriods,
  };

  return {
    aWarnings: computeChangeWarnings({
      ...shared,
      reservation: input.a,
      target: input.aTarget,
    }),
    bWarnings: computeChangeWarnings({
      ...shared,
      reservation: input.b,
      target: input.bTarget,
    }),
  };
}

/** Convenience: highest level among a list of warnings. */
export function warningSeverity(warnings: MoveWarning[]): WarningLevel | null {
  if (warnings.some((w) => w.level === "block")) return "block";
  if (warnings.some((w) => w.level === "warn")) return "warn";
  if (warnings.some((w) => w.level === "info")) return "info";
  return null;
}

/** Convenience: true if any warning blocks the action. */
export function hasBlockingWarning(warnings: MoveWarning[]): boolean {
  return warnings.some((w) => w.level === "block");
}
